import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Messages } from 'primereact/messages';
import { Button } from 'primereact/button';
import { Form } from 'react-bootstrap';
import passwordResetApi from '../api/passwordResetApi';
import { flagError } from '../utils/flagSystem';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const email = searchParams.get('email');
    const code = searchParams.get('code');
    const type = searchParams.get('type');
    const validLink = Boolean(email && code && type);
    const creating = type === 'new';
    const [password, setPassword] = useState('');
    const [passwordRepeat, setPasswordRepeat] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [seconds, setSeconds] = useState(5);
    const msgs = useRef(null);
    const pending = useRef(false);
    const currentLink = useRef(null);
    const link = searchParams.toString();
    currentLink.current = link;
    const mounted = useRef(false);
    const navigate = useNavigate();

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);
    useEffect(() => {
        setPassword(''); setPasswordRepeat(''); setSuccess(false); setSeconds(5);
        if (msgs.current) msgs.current.clear();
    }, [link]);
    useEffect(() => {
        if (!success) return;
        const timer = window.setTimeout(() => {
            if (seconds <= 1) navigate('/login');
            else setSeconds(value => value - 1);
        }, 1000);
        return () => window.clearTimeout(timer);
    }, [success, seconds, navigate]);

    const handleSubmit = async event => {
        event.preventDefault();
        if (pending.current || success) return;
        msgs.current.clear();
        if (!validLink || !password || password !== passwordRepeat) {
            msgs.current.show({ severity: 'error', detail: !validLink ? 'The password link is incomplete. Request a new email.' :
                !password ? 'Choose a password.' : 'Passwords do not match', sticky: true });
            return;
        }
        pending.current = true;
        setLoading(true);
        try {
            const result = await passwordResetApi.reset({ email, code, password });
            if (!mounted.current || currentLink.current !== link) return;
            setPassword(''); setPasswordRepeat('');
            msgs.current.show({ severity: 'success', detail: creating ?
                'Your password has been created. You can now sign in.' : result.message, sticky: true });
            setSuccess(true);
        } catch (error) {
            if (mounted.current && currentLink.current === link) {
                msgs.current.show({ severity: 'error', detail: flagError(error, 'Unable to change your password. Please try again.'), sticky: true });
            }
        } finally {
            pending.current = false;
            if (mounted.current) setLoading(false);
        }
    };

    return <div className="container center">
        <div className="p-d-flex p-flex-column p-jc-center p-ai-center">
            <h3>{creating ? 'Create your password' : 'Reset your password'}</h3>
            <Messages ref={msgs} />
            {!validLink ? <div role="alert">
                <p>The password link is incomplete. Request a new password-setup email or use Forgot Password.</p>
                <Link to="/forgot-password">Forgot Password</Link>
            </div> : success ? <div className="w-100 mt-2">
                <p className="text-center">Redirecting to login in {seconds} seconds...</p>
                <Link to="/login">Go to login now</Link>
            </div> : <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="formBasicPassword">
                    <Form.Label>{creating ? 'Choose a password' : 'New Password'}</Form.Label>
                    <Form.Control type="password" autoComplete="new-password" value={password} disabled={loading}
                        onChange={event => setPassword(event.target.value)} required />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formBasicRepeatPassword">
                    <Form.Label>Confirm password</Form.Label>
                    <Form.Control type="password" autoComplete="new-password" value={passwordRepeat} disabled={loading}
                        onChange={event => setPasswordRepeat(event.target.value)} required />
                </Form.Group>
                <Button type="submit" label={creating ? 'Create Password' : 'Reset Password'} disabled={loading} loading={loading} />
            </Form>}
            <div className="w-100 mt-2"><Link to="/login">Go to login</Link></div>
        </div>
    </div>;
};

export default ResetPassword;
